import { useMemo, useState } from 'react';
import { complaintsApi } from '../../api/complaints';
import { useAsync, Banner } from './ui';
import MaterialsReportDoc, { type MaterialsReportLine } from '../../components/MaterialsReportDoc';
import MaterialsSummaryDoc, { type MaterialsSummaryLine } from '../../components/MaterialsSummaryDoc';
import { shareMaterialsReportPdf } from '../../lib/shareMaterialsReport';
import { shareMaterialsSummaryPdf } from '../../lib/shareMaterialsSummary';

type Row = Record<string, unknown>;
type View = 'detail' | 'summary';
const daysAgoISO = (n: number) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

function toLines(v: unknown): Array<{ item: string; qty: number | '' }> {
  return Array.isArray(v) ? (v as Array<{ item: string; qty: number | '' }>) : [];
}

/** Every material used and every condemned item, grouped by the complaint it was recorded
 * against — the department's stock/consumption record. Printable (A4) and shareable as PDF.
 * A second view totals the same data by material name, for "how much of X got used". */
export default function MaterialsReportAdmin() {
  const [view, setView] = useState<View>('detail');
  const [from, setFrom] = useState(daysAgoISO(30));
  const [to, setTo] = useState(daysAgoISO(0));
  const [complaintNo, setComplaintNo] = useState('');
  const [applied, setApplied] = useState({ from, to, complaintNo: '' });
  const complaints = useAsync(() => complaintsApi.list('resolved'), [], 'complaintsResolved');

  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfMsg, setPdfMsg] = useState('');

  const rows: MaterialsReportLine[] = useMemo(() => {
    const fromMs = applied.from ? new Date(applied.from).getTime() : -Infinity;
    const toMs = applied.to ? new Date(applied.to).getTime() + 86400000 : Infinity;
    const search = applied.complaintNo.trim().toUpperCase();
    return (complaints.data?.rows ?? [])
      .map((r: Row) => {
        const materialsUsed = toLines(r['Materials Used']);
        const condemnedItems = toLines(r['Condemned Items']);
        const date = String(r['Updated At'] ?? r['Complaint Date'] ?? '');
        return {
          complaintId: String(r['Complaint ID']), date,
          department: String(r['Department'] ?? ''), location: String(r['Location'] ?? ''),
          details: String(r['Details'] ?? ''),
          materialsUsed, condemnedItems,
          resolvedBy: String(r['Assigned To Name'] ?? ''),
        };
      })
      .filter((r) => r.materialsUsed.length > 0 || r.condemnedItems.length > 0)
      .filter((r) => !search || r.complaintId.toUpperCase().includes(search))
      .filter((r) => {
        const t = new Date(r.date).getTime();
        return Number.isNaN(t) || (t >= fromMs && t < toMs);
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [complaints.data, applied]);

  // Totals by material name (case/whitespace-insensitive so "LED Bulb" and "led bulb" merge),
  // keeping the first-seen spelling for display — "how much of X got used" across the range.
  const summaryRows: MaterialsSummaryLine[] = useMemo(() => {
    const byKey = new Map<string, MaterialsSummaryLine>();
    const add = (item: string, qty: number | '', field: 'usedQty' | 'condemnedQty') => {
      const name = item.trim();
      if (!name) return;
      const key = name.toLowerCase();
      const line = byKey.get(key) ?? { item: name, usedQty: 0, condemnedQty: 0 };
      line[field] += qty === '' || qty == null ? 0 : Number(qty);
      byKey.set(key, line);
    };
    rows.forEach((r) => {
      r.materialsUsed.forEach((l) => add(l.item, l.qty, 'usedQty'));
      r.condemnedItems.forEach((l) => add(l.item, l.qty, 'condemnedQty'));
    });
    return Array.from(byKey.values()).sort((a, b) => b.usedQty + b.condemnedQty - (a.usedQty + a.condemnedQty) || a.item.localeCompare(b.item));
  }, [rows]);

  async function sharePdf() {
    setPdfBusy(true); setPdfMsg('');
    try {
      const how = view === 'detail'
        ? await shareMaterialsReportPdf({ from: applied.from, to: applied.to, rows })
        : await shareMaterialsSummaryPdf({ from: applied.from, to: applied.to, rows: summaryRows });
      if (how === 'downloaded') setPdfMsg('PDF downloaded.');
    } catch (e) {
      if ((e as Error)?.name !== 'AbortError') setPdfMsg('Could not create the PDF: ' + ((e as Error)?.message ?? e));
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <div className="dailyDoc">
      <div className="screen-only">
        <h1>Material used &amp; condemned report</h1>
        <p className="muted tiny" style={{ marginTop: -6 }}>
          {view === 'detail'
            ? 'One row per resolved complaint, with every material used and every condemned item recorded against it.'
            : 'Every material name totalled across the range — how much of each got used and how much got condemned.'}
        </p>
        <div className="choice-group" style={{ margin: '10px 0 14px' }}>
          <button type="button" className={`choice-btn ${view === 'detail' ? 'on' : ''}`} onClick={() => setView('detail')}>By complaint</button>
          <button type="button" className={`choice-btn ${view === 'summary' ? 'on' : ''}`} onClick={() => setView('summary')}>By material (totals)</button>
        </div>
        <div className="filters">
          <div><label>From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><label>To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          {view === 'detail' && (
            <div><label>Complaint No</label><input value={complaintNo} onChange={(e) => setComplaintNo(e.target.value)} placeholder="e.g. T10234" /></div>
          )}
          <button className="sm" onClick={() => setApplied({ from, to, complaintNo })}>Apply</button>
          <button className="secondary sm" onClick={() => window.print()} disabled={complaints.loading}>🖨 Print (A4)</button>
          <button className="secondary sm" onClick={sharePdf} disabled={complaints.loading || pdfBusy}>
            {pdfBusy ? 'Preparing…' : '📤 Share PDF'}
          </button>
        </div>
        <Banner error={complaints.error || pdfMsg} ok={pdfMsg === 'PDF downloaded.' ? pdfMsg : undefined} />
        {complaints.loading && <div className="spinner" />}
      </div>

      {!complaints.loading && view === 'detail' && <MaterialsReportDoc from={applied.from} to={applied.to} rows={rows} />}
      {!complaints.loading && view === 'summary' && <MaterialsSummaryDoc from={applied.from} to={applied.to} rows={summaryRows} />}
    </div>
  );
}
